import { auth, db } from "../lib/firebase";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, updateProfile, GoogleAuthProvider, signInWithPopup, sendPasswordResetEmail } from "firebase/auth";
import type { User } from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { ensureReferralCode } from './referrals';
import { sendWelcomeEmail, notifyAdmins, sendVerificationCode } from './email';

export type UserRole = "buyer" | "seller" | "admin";

export async function signUpWithEmail(email: string, password: string, role: UserRole, extra?: { fullName?: string; username?: string; phone?: string; university?: string; referredByCode?: string }) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  if (extra?.fullName) {
    try { await updateProfile(cred.user, { displayName: extra.fullName }); } catch {}
  }
  // Create user profile doc. If this fails (rules / network), delete the auth user so we
  // don't leave behind an "orphan" account that has Firebase auth but no Firestore profile.
  try {
    await setDoc(doc(db, "users", cred.user.uid), {
      email,
      role,
      fullName: extra?.fullName || null,
      username: extra?.username || null,
      phone: extra?.phone || null,
      university: extra?.university || null,
      createdAt: Date.now(),
      status: role === 'buyer' ? 'active' : 'pending',
      wallet: {
        balance: 0,
        escrowBalance: 0,
        totalEarnings: 0,
      },
    }, { merge: true });
  } catch (e) {
    try { await cred.user.delete(); } catch {}
    throw new Error('Could not create your profile. Please try again.');
  }
  // Seed dedicated wallet doc for wallet pages/services
  try {
    await setDoc(doc(db, "wallets", cred.user.uid), {
      balance: 0,
      escrowBalance: 0,
      totalEarnings: 0,
    }, { merge: true });
  } catch (e) {
    // Wallet doc is non-critical at signup; surface a warning but keep the user.
    console.warn('Failed to seed wallet doc on signup:', e);
  }

  // Ensure this new user has a referral code for sharing
  try {
    await ensureReferralCode(cred.user.uid);
  } catch {}

  // If a referredByCode was provided, attribute the referral
  if (extra?.referredByCode) {
    try {
      const { resolveReferrerByCode, recordReferralByType } = await import('./referrals');
      const resolved = await resolveReferrerByCode(extra.referredByCode.trim());
      if (resolved) {
        // Only set referredBy for user referrers (wallet rewards target users)
        if (resolved.type === 'user') {
          await updateDoc(doc(db, 'users', cred.user.uid), { referredBy: resolved.id });
        } else {
          await updateDoc(doc(db, 'users', cred.user.uid), { referredByMarketer: resolved.id });
        }
        await recordReferralByType(resolved, cred.user.uid);
      }
    } catch {}
  }

  // Send our own 6-digit verification code via Resend/SMTP (replaces Firebase's
  // hosted email-link flow).
  try {
    await sendVerificationCode(email, extra?.fullName || cred.user.displayName || undefined);
  } catch (e) {
    console.warn('Verification code send failed at signup', e);
  }

  try {
    if (role === 'buyer' || role === 'seller') {
      await sendWelcomeEmail(cred.user.uid, email, extra?.fullName || cred.user.displayName || undefined, role);
    }
  } catch {}
  // Notify admins (fire-and-forget)
  notifyAdmins({
    event: `new_${role}_signup`,
    subject: `New ${role} signed up: ${extra?.fullName || email}`,
    text: `A new ${role} just signed up on Campus Market.`,
    meta: {
      uid: cred.user.uid,
      email,
      name: extra?.fullName || cred.user.displayName || '',
      phone: extra?.phone || '',
      university: extra?.university || '',
      role,
    },
  });
  return cred.user;
}

export async function signInWithEmail(email: string, password: string) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

export async function signOutUser() {
  await signOut(auth);
}

export function subscribeAuth(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback);
}

export async function getUserRole(uid: string): Promise<{ role: UserRole | null; status: string | null }> {
  const snap = await getDoc(doc(db, "users", uid));
  if (snap.exists()) {
    const data = snap.data();
    return { role: (data.role as UserRole) || null, status: data.status || null };
  }
  return { role: null, status: null };
}

export async function signInWithGoogle(defaultRole: UserRole = "buyer") {
  const provider = new GoogleAuthProvider();
  const cred = await signInWithPopup(auth, provider);
  const uid = cred.user.uid;
  const profileRef = doc(db, "users", uid);
  const existing = await getDoc(profileRef);
  if (!existing.exists()) {
    await setDoc(profileRef, {
      email: cred.user.email,
      role: defaultRole,
      fullName: cred.user.displayName || null,
      createdAt: Date.now(),
      status: defaultRole === 'buyer' ? 'active' : 'pending',
      wallet: {
        balance: 0,
        escrowBalance: 0,
        totalEarnings: 0,
      },
    }, { merge: true });
    // Also seed wallet doc
    await setDoc(doc(db, "wallets", uid), {
      balance: 0,
      escrowBalance: 0,
      totalEarnings: 0,
    }, { merge: true });
    try {
      if (defaultRole === 'buyer' || defaultRole === 'seller') {
        await sendWelcomeEmail(uid, cred.user.email || '', cred.user.displayName || undefined, defaultRole);
      }
    } catch {}
    // Notify admins of the new Google signup
    notifyAdmins({
      event: `new_${defaultRole}_signup_google`,
      subject: `New ${defaultRole} signed up via Google: ${cred.user.displayName || cred.user.email}`,
      text: `A new ${defaultRole} just signed up via Google on Campus Market.`,
      meta: {
        uid,
        email: cred.user.email || '',
        name: cred.user.displayName || '',
        provider: 'google',
        role: defaultRole,
      },
    });
  }
  return cred.user;
}

export async function upgradeToSeller(uid: string, extra?: { university?: string }) {
  await setDoc(doc(db, "users", uid), {
    role: "seller",
    university: extra?.university || null,
  }, { merge: true });
}

export async function getUserProfile(uid: string): Promise<{
  fullName: string | null;
  username?: string | null;
  email: string | null;
  isVerified?: boolean;
  status?: string;
  rejectionReason?: string | null;
  rejectionDate?: any;
  approvalDate?: any;
  university?: string | null;
  phone?: string | null;
  studentIdUrl?: string | null;
  studentNumber?: string | null;
  photoURL?: string | null;
  bio?: string | null;
} > {
  const snap = await getDoc(doc(db, "users", uid));
  if (snap.exists()) {
    const data = snap.data();
    return {
      fullName: data.fullName || null,
      username: data.username || null,
      email: data.email || null,
      isVerified: data.isVerified || false,
      status: data.status || null,
      rejectionReason: data.rejectionReason || null,
      rejectionDate: data.rejectionDate || null,
      approvalDate: data.approvalDate || null,
      university: data.university || null,
      phone: data.phone || null,
      studentIdUrl: data.studentIdUrl || null,
      studentNumber: data.studentNumber || null,
      photoURL: data.photoURL || null,
      bio: data.bio || null,
    };
  }
  return { fullName: null, email: null };
}

// Sign in as admin using Firebase Auth + custom claims (admin: true).
// First try the user's existing token; if no admin claim, attempt to bootstrap via the
// /api/admin/grant endpoint (which will succeed only if the email is on
// INITIAL_ADMIN_EMAILS or another admin promoted them).
export async function signInAsAdmin(email: string, password: string): Promise<{ user: any; isAdmin: boolean }> {
  const user = await signInWithEmail(email, password);

  // First check current claims.
  let token = await user.getIdTokenResult();
  if (token.claims.admin === true) {
    return { user, isAdmin: true };
  }

  // Try to bootstrap: backend will allow self-grant if the email is allowlisted.
  try {
    const idToken = await user.getIdToken();
    const resp = await fetch('/api/admin/grant', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({}),
    });
    if (resp.ok) {
      // Force a fresh token so the new claim becomes visible client-side.
      token = await user.getIdTokenResult(true);
      if (token.claims.admin === true) {
        return { user, isAdmin: true };
      }
    }
  } catch { /* ignore — fall through to "not admin" */ }

  // Legacy fallback: a Firestore role==='admin' was set previously.
  try {
    const snap = await getDoc(doc(db, 'users', user.uid));
    if (snap.exists() && (snap.data() as any)?.role === 'admin') {
      // They have the role marker but no claim yet — backend should have set claim above.
      // Treat as admin for this session; future sessions will pick up the claim.
      return { user, isAdmin: true };
    }
  } catch {}

  await signOutUser();
  throw new Error('Access denied. Admin privileges required.');
}

// Returns true if the *currently signed-in* user has the admin claim.
// Pass forceRefresh=true after granting to bypass the cached token.
export async function isCurrentUserAdmin(forceRefresh = false): Promise<boolean> {
  const u = auth.currentUser;
  if (!u) return false;
  try {
    const t = await u.getIdTokenResult(forceRefresh);
    if (t.claims.admin === true) return true;
  } catch {}
  // Legacy mirror in Firestore.
  try {
    const snap = await getDoc(doc(db, 'users', u.uid));
    return snap.exists() && (snap.data() as any)?.role === 'admin';
  } catch { return false; }
}

// Backwards-compatible per-uid check (used by some pages). Prefer isCurrentUserAdmin().
export async function isUserAdmin(uid: string): Promise<boolean> {
  const u = auth.currentUser;
  if (u && u.uid === uid) return isCurrentUserAdmin();
  // Cannot read another user's claims from the client; fall back to Firestore mirror.
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    return snap.exists() && (snap.data() as any)?.role === 'admin';
  } catch { return false; }
}

// Initialize admin user (run once to create admin account)
export async function initializeAdminAccount() {
  const adminEmail = "Joe@campusmarket.com";
  const adminPassword = "Admin123";
  
  try {
    // Check if admin already exists
    const adminQuery = await getDoc(doc(db, "users", "admin"));
    
    if (!adminQuery.exists()) {
      // Create admin user
      const cred = await createUserWithEmailAndPassword(auth, adminEmail, adminPassword);
      
      await setDoc(doc(db, "users", cred.user.uid), {
        email: adminEmail,
        role: "admin",
        fullName: "Campus Market Administrator",
        createdAt: Date.now(),
        status: "active",
        wallet: {
          balance: 0,
          escrowBalance: 0,
          totalEarnings: 0,
        },
      }, { merge: true });
      
      console.log("Admin account created successfully");
      return cred.user;
    } else {
      console.log("Admin account already exists");
      return null;
    }
  } catch (error) {
    console.error("Error creating admin account:", error);
    throw error;
  }
}

export async function updateUserProfile(uid: string, data: { fullName?: string | null; username?: string | null; phone?: string | null; university?: string | null; photoURL?: string | null; bio?: string | null; }): Promise<void> {
  await setDoc(doc(db, 'users', uid), {
    ...(data.fullName !== undefined ? { fullName: data.fullName } : {}),
    ...(data.username !== undefined ? { username: data.username } : {}),
    ...(data.phone !== undefined ? { phone: data.phone } : {}),
    ...(data.university !== undefined ? { university: data.university } : {}),
    ...(data.photoURL !== undefined ? { photoURL: data.photoURL } : {}),
    ...(data.bio !== undefined ? { bio: data.bio } : {}),
    updatedAt: Date.now(),
  }, { merge: true });

  try {
    if (data.fullName) {
      const { auth } = await import('../lib/firebase');
      const { updateProfile } = await import('firebase/auth');
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { displayName: data.fullName || undefined });
      }
    }
  } catch {}
}

// Re-sends the 6-digit verification code to the currently signed-in user's email.
// (The old Firebase-hosted link flow has been replaced.)
export async function sendVerificationEmail(): Promise<void> {
  const user = auth.currentUser;
  if (!user || !user.email) throw new Error('No signed-in user with email.');
  await sendVerificationCode(user.email, user.displayName || undefined);
}

export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

// Verifies the 6-digit code and sets the new password (server-side via Admin SDK).
// Throws with the server's error message on failure so the UI can show it.
export async function resetPasswordByCode(email: string, code: string, newPassword: string): Promise<boolean> {
  const resp = await fetch('/api/email/reset-password-by-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, newPassword }),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new Error(data?.error || 'Failed to reset password.');
  }
  return !!(data && data.ok === true);
}
